// src/pages/StateAdminReport/AllStateHeads.jsx
import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import MapIcon from '@mui/icons-material/Map';
import PinDropIcon from '@mui/icons-material/PinDrop';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';

// Helper to format State Head ID to last 6 digits uppercase if not already set
const formatStateHeadId = (id, stateHeadIdField) => {
  if (stateHeadIdField && stateHeadIdField.trim() !== '') return stateHeadIdField;
  if (!id) return 'N/A';
  const str = String(id).trim();
  return str.slice(-6).toUpperCase();
};

const AllStateHeads = () => {
  const { can } = usePermissions();
  const [userLevel, setUserLevel] = useState(null);
  const [userId, setUserId] = useState('');
  const [stateHeads, setStateHeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Dialog State for viewing Districts, Constituencies, or Pincodes
  const [dialogState, setDialogState] = useState({
    open: false,
    title: '',
    stateHeadName: '',
    items: []
  });
  const [dialogFilter, setDialogFilter] = useState('');

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserLevel(decoded.level);
        setUserId(decoded.userId);
      } catch (err) {
        console.error('Invalid token', err);
      }
    }
  }, []);


  const fetchStateHeads = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${API_BASE_URL}/users/by-level`, {
        params: { level: 2, requesterLevel: userLevel || 1, userId }
      });
      if (res.status === 200 && Array.isArray(res.data)) {
        setStateHeads(res.data);
      } else {
        setStateHeads([]);
      }
    } catch (err) {
      console.error('Error fetching state heads:', err);
      setError('Failed to fetch State Head records.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userLevel !== null) {
      fetchStateHeads();
    }
  }, [userLevel, userId]);

  // Search Filter by State Head ID / Name / Mobile / Email
  const filteredStateHeads = useMemo(() => {
    if (!searchTerm.trim()) return stateHeads;
    const term = searchTerm.toLowerCase().trim();
    return stateHeads.filter((sh) => {
      const shId = (sh.stateHeadId || sh.displayId || formatStateHeadId(sh._id || sh.id, sh.stateHeadId)).toLowerCase();
      const fName = (sh.firstName || '').toLowerCase();
      const lName = (sh.lastName || '').toLowerCase();
      const fullName = `${fName} ${lName}`;
      const mobile = String(sh.phoneNumber || sh.mobile || '').toLowerCase();
      const email = (sh.email || '').toLowerCase();

      return (
        shId.includes(term) ||
        fName.includes(term) ||
        lName.includes(term) ||
        fullName.includes(term) ||
        mobile.includes(term) ||
        email.includes(term)
      );
    });
  }, [stateHeads, searchTerm]);

  // Open Dialog for Districts / Constituencies / Pincodes
  const handleOpenDialog = (title, stateHeadName, items) => {
    setDialogState({
      open: true,
      title,
      stateHeadName,
      items: items || []
    });
    setDialogFilter('');
  };

  const handleCloseDialog = () => {
    setDialogState({
      open: false,
      title: '',
      stateHeadName: '',
      items: []
    });
    setDialogFilter('');
  };

  // Filtered dialog items
  const filteredDialogItems = useMemo(() => {
    if (!dialogFilter.trim()) return dialogState.items;
    const q = dialogFilter.toLowerCase().trim();
    return dialogState.items.filter(item => String(item).toLowerCase().includes(q));
  }, [dialogState.items, dialogFilter]);

  // CSV Export (permission gated)
  const handleExportCSV = () => {
    if (!can('stateHead.all.export')) return;
    if (filteredStateHeads.length === 0) return;

    const headers = [
      'SL No',
      'State Head ID',
      'First Name',
      'Last Name',
      'Mobile Number',
      'Email',
      'List of District',
      'List of Constituency',
      'List of Pincodes'
    ];

    const csvRows = [headers.join(',')];

    filteredStateHeads.forEach((sh, idx) => {
      const displayShId = sh.stateHeadId || sh.displayId || formatStateHeadId(sh._id || sh.id, sh.stateHeadId);
      const dists = Array.isArray(sh.districts) && sh.districts.length > 0 ? sh.districts.join('; ') : 'N/A';
      const vills = Array.isArray(sh.villages) && sh.villages.length > 0 ? sh.villages.join('; ') : 'N/A';
      const pins = Array.isArray(sh.pincodes) && sh.pincodes.length > 0 ? sh.pincodes.join('; ') : (sh.pinCode ? String(sh.pinCode) : 'N/A');

      const row = [
        idx + 1,
        `"${displayShId}"`,
        `"${sh.firstName || ''}"`,
        `"${sh.lastName || ''}"`,
        `"${sh.phoneNumber || sh.mobile || ''}"`,
        `"${sh.email || ''}"`,
        `"${dists}"`,
        `"${vills}"`,
        `"${pins}"`
      ];

      csvRows.push(row.join(','));
    });

    const csvContent = 'data:text/csv;charset=utf-8,' + csvRows.join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `State_Heads_Report_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box sx={{ p: 3, position: 'relative' }}>
      <BackButton />
      <br /><br />

      {/* Header & Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          All STATE HEAD
        </Typography>

        {/* CSV Export (permission gated) */}
        {can('stateHead.all.export') && (
          <Button
            variant="contained"
            color="success"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
            disabled={filteredStateHeads.length === 0}
          >
            Export CSV
          </Button>
        )}
      </Box>

      {/* Search Bar */}
      <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SearchIcon color="action" />
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Search by State Head ID, Name, Mobile, or Email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2, maxHeight: 'calc(100vh - 260px)', minHeight: 450, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead sx={{ backgroundColor: 'primary.main' }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>SL No</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>State Head ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>First Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Last Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Mobile Number</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Email</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>List of DISTRICT</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>List of Constituency</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>List of PINCODE’s</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredStateHeads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                    No State Head records found.
                  </TableCell>
                </TableRow>
              ) : (
                filteredStateHeads.map((sh, idx) => {
                  const displayShId = sh.stateHeadId || sh.displayId || formatStateHeadId(sh._id || sh.id, sh.stateHeadId);
                  const nameStr = `${sh.firstName || ''} ${sh.lastName || ''}`.trim() || 'State Head';
                  const distList = Array.isArray(sh.districts) && sh.districts.length > 0 ? sh.districts : [];
                  const villList = Array.isArray(sh.villages) && sh.villages.length > 0 ? sh.villages : [];
                  const pinList = Array.isArray(sh.pincodes) && sh.pincodes.length > 0 ? sh.pincodes : (sh.pinCode ? [String(sh.pinCode)] : []);

                  return (
                    <TableRow key={sh._id || sh.id || idx} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        {displayShId}
                      </TableCell>
                      <TableCell>{sh.firstName || 'N/A'}</TableCell>
                      <TableCell>{sh.lastName || 'N/A'}</TableCell>
                      <TableCell>{sh.phoneNumber || sh.mobile || 'N/A'}</TableCell>
                      <TableCell>{sh.email || 'N/A'}</TableCell>
                      
                      {/* District List Button */}
                      <TableCell>
                        {distList.length > 0 ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            startIcon={<LocationCityIcon />}
                            onClick={() => handleOpenDialog('Districts', nameStr, distList)}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                          >
                            View Districts ({distList.length})
                          </Button>
                        ) : (
                          <Typography variant="caption" color="textSecondary">N/A</Typography>
                        )}
                      </TableCell>

                      {/* Constituency List Button */}
                      <TableCell>
                        {villList.length > 0 ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            startIcon={<MapIcon />}
                            onClick={() => handleOpenDialog('Constituencies', nameStr, villList)}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                          >
                            View Constituencies ({villList.length})
                          </Button>
                        ) : (
                          <Typography variant="caption" color="textSecondary">N/A</Typography>
                        )}
                      </TableCell>

                      {/* Pincodes List Button */}
                      <TableCell>
                        {pinList.length > 0 ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="info"
                            startIcon={<PinDropIcon />}
                            onClick={() => handleOpenDialog('Pincodes', nameStr, pinList)}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                          >
                            View Pincodes ({pinList.length})
                          </Button>
                        ) : (
                          <Typography variant="caption" color="textSecondary">N/A</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog Modal for Viewing Districts / Constituencies / Pincodes */}
      <Dialog open={dialogState.open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', backgroundColor: '#1e293b', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Assigned {dialogState.title} — {dialogState.stateHeadName}</span>
          <Chip label={`Total: ${dialogState.items.length}`} color="primary" size="small" sx={{ fontWeight: 'bold' }} />
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3, backgroundColor: '#f8fafc' }}>
          {dialogState.items.length > 5 && (
            <TextField
              fullWidth
              size="small"
              placeholder={`Filter ${dialogState.title}...`}
              value={dialogFilter}
              onChange={(e) => setDialogFilter(e.target.value)}
              sx={{ mb: 2, backgroundColor: 'white' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
          )}

          {filteredDialogItems.length === 0 ? (
            <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 3 }}>
              No matching {dialogState.title.toLowerCase()} found.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', maxHeight: 350, overflowY: 'auto', p: 1 }}>
              {filteredDialogItems.map((item, idx) => (
                <Chip
                  key={idx}
                  label={item}
                  color={
                    dialogState.title === 'Districts' ? 'primary' :
                    dialogState.title === 'Constituencies' ? 'secondary' : 'info'
                  }
                  variant="outlined"
                  sx={{ fontWeight: 600, fontSize: '0.85rem' }}
                />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="contained" onClick={handleCloseDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AllStateHeads;
